import { Flex, Text } from '@radix-ui/themes';
import { toast } from 'sonner';
import { usePage } from '@inertiajs/react';
import { useTranslation } from 'react-i18next';
import logoUrl from '@/images/lawfi-logo.svg';
import {
  BankIcon,
  BuildingsIcon,
  ChartLineUpIcon,
  CurrencyCircleDollarIcon,
  FilesIcon,
  GearIcon,
  HomeIcon,
  LockIcon,
  MFAIcon,
  MoneyIcon,
  MoneyWavyIcon,
  PortfolioIcon,
  QrCodeIcon,
  ScrollIcon,
  TransactionsIcon,
  UsersIcon,
  UsersThreeIcon,
  LoanApplicationsIcon,
} from '@/components/icons';
import Link from '@/components/Link';
import cn from '@/lib/cn';

import {
  account_user_path,
  admin_bank_accounts_path,
  admin_fee_batches_path,
  admin_firms_path,
  admin_fund_batches_path,
  admin_loans_path,
  admin_portfolios_path,
  admin_root_path,
  admin_transfers_path,
  admin_users_path,
  applications_firm_loans_path,
  edit_firm_path,
  firm_bank_accounts_path,
  firm_documents_path,
  firm_employments_path,
  firm_loans_path,
  firm_offices_path,
  firm_path,
  firm_stakeholders_path,
  firm_tools_path,
  firm_transactions_path,
  mfa_user_path,
  revenue_admin_reports_path,
  root_path,
} from '@/lib/routes';

export default function Sidebar({ setSidebarOpen }) {
  const { t } = useTranslation();
  const { url, props, component: currentPage } = usePage();
  const { current_user, firm, features } = props;
  const isAdmin = current_user.admin;
  const isAdminNamespace = url.startsWith('/admin');
  const isUserRoute = url.startsWith('/user/');
  const isFirmApproved = firm?.state === 'approved';

  const NavLinkItem = ({ name, href, current, icon, locked }) => {
    const Icon = icon;

    return (
      <li key={name}>
        <Link
          href={locked ? '#' : href}
          onClick={(event) => {
            if (locked) {
              event.preventDefault();
              toast.warning(t('firm.locked_until_approved'));
            } else if (setSidebarOpen) setSidebarOpen(false);
          }}
          className={cn(
            current
              ? 'bg-gray-50 text-teal-600'
              : 'text-gray-700 hover:bg-gray-50 hover:text-teal-600',
            locked ? 'opacity-40 cursor-default hover:text-gray-700' : '',
            'group flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6 no-underline',
          )}
        >
          {locked ? (
            <LockIcon className="h-6 w-6 shrink-0" />
          ) : (
            <Icon
              aria-hidden="true"
              className={cn(
                current
                  ? 'text-teal-600'
                  : 'text-gray-400 group-hover:text-teal-600',
                locked ? 'group-hover:text-gray-400' : '',
                'h-6 w-6 shrink-0',
              )}
            />
          )}
          {name}
        </Link>
      </li>
    );
  };

  const FirmNav = () => {
    return (
      <>
        {isAdmin && firm && (
          <Flex className="-mx-2 rounded-md bg-gray-900 px-3 py-1">
            <Text className="text-sm/6 text-white">
              {t('admin.operating_as')}{' '}
              <Link href={admin_root_path()} className="text-white font-bold">
                {t('common.admin')}
              </Link>
            </Text>
          </Flex>
        )}
        <nav className="flex flex-1 flex-col">
          <ul className="flex flex-1 flex-col gap-y-7">
            <li>
              <ul className="-mx-2 space-y-1">
                <NavLinkItem
                  name={t('common.dashboard')}
                  href={firm ? firm_path(firm.slug) : root_path()}
                  icon={HomeIcon}
                  current={currentPage === 'firms/DashboardPage'}
                />
                {firm && (
                  <>
                    <NavLinkItem
                      name={t('loan.applications')}
                      href={applications_firm_loans_path(firm.slug)}
                      icon={LoanApplicationsIcon}
                      current={currentPage === 'loans/ApplicationsPage'}
                      locked={!isFirmApproved}
                    />

                    <NavLinkItem
                      name={t('common.loans')}
                      href={firm_loans_path(firm.slug)}
                      icon={MoneyWavyIcon}
                      current={currentPage === 'loans/IndexPage'}
                      locked={!isFirmApproved}
                    />

                    <NavLinkItem
                      name={t('tools.heading')}
                      href={firm_tools_path(firm.slug)}
                      icon={QrCodeIcon}
                      current={currentPage === 'tools/IndexPage'}
                      locked={!isFirmApproved}
                    />
                  </>
                )}
              </ul>
            </li>
            {firm && (
              <>
                <li>
                  <div className="text-xs font-semibold leading-6 text-gray-400">
                    {t('common.law_firm')}
                  </div>
                  <ul className="-mx-2 mt-2 space-y-1">
                    <NavLinkItem
                      name={t('common.profile')}
                      href={edit_firm_path(firm.slug)}
                      icon={ScrollIcon}
                      current={currentPage === 'firms/EditPage'}
                    />
                    <NavLinkItem
                      name={t('common.bank_accounts')}
                      href={firm_bank_accounts_path(firm.slug)}
                      icon={BankIcon}
                      current={currentPage === 'bank_accounts/IndexPage'}
                    />
                    <NavLinkItem
                      name={t('common.documents')}
                      href={firm_documents_path(firm.slug)}
                      icon={FilesIcon}
                      current={currentPage === 'documents/IndexPage'}
                    />
                    <NavLinkItem
                      name={t('firm.stakeholders')}
                      href={firm_stakeholders_path(firm.slug)}
                      icon={UsersIcon}
                      current={currentPage === 'stakeholders/IndexPage'}
                    />
                    <NavLinkItem
                      name={t('offices.heading')}
                      href={firm_offices_path(firm.slug)}
                      icon={BuildingsIcon}
                      current={currentPage === 'offices/IndexPage'}
                    />
                  </ul>
                </li>
                <li>
                  <div className="text-xs font-semibold leading-6 text-gray-400">
                    {t('common.reporting')}
                  </div>
                  <ul className="-mx-2 mt-2 space-y-1">
                    <NavLinkItem
                      name={t('transactions.heading')}
                      href={firm_transactions_path(firm.slug)}
                      icon={TransactionsIcon}
                      current={currentPage === 'transactions/IndexPage'}
                    />
                  </ul>
                </li>
                <li>
                  <div className="text-xs font-semibold leading-6 text-gray-400">
                    {t('common.settings')}
                  </div>
                  <ul className="-mx-2 mt-2 space-y-1">
                    <NavLinkItem
                      name={t('common.users')}
                      href={firm_employments_path(firm.slug)}
                      icon={UsersThreeIcon}
                      current={currentPage === 'employments/IndexPage'}
                    />

                    <NavLinkItem
                      name={t('common.configuration')}
                      href={`/firms/${firm.slug}/configuration`}
                      icon={GearIcon}
                      current={currentPage === 'firms/ConfigurationPage'}
                    />
                  </ul>
                </li>
              </>
            )}
          </ul>
        </nav>
      </>
    );
  };

  const AdminNav = () => {
    return (
      <nav className="flex flex-1 flex-col">
        <ul className="flex flex-1 flex-col gap-y-7">
          <li>
            <ul className="-mx-2 space-y-1">
              <NavLinkItem
                name={t('common.dashboard')}
                href={admin_root_path()}
                icon={HomeIcon}
                current={currentPage === 'admin/DashboardPage'}
              />
              <NavLinkItem
                name={'Law Firms'}
                href={admin_firms_path()}
                icon={BuildingsIcon}
                current={currentPage === 'admin/firms/IndexPage'}
              />
              <NavLinkItem
                name={'Loans'}
                href={admin_loans_path()}
                icon={MoneyWavyIcon}
                current={currentPage === 'admin/loans/IndexPage'}
              />
            </ul>
          </li>
          <li>
            <div className="text-xs font-semibold leading-6 text-gray-400">
              Transfers
            </div>
            <ul className="-mx-2 mt-2 space-y-1">
              <NavLinkItem
                name={'Funds'}
                href={admin_fund_batches_path()}
                icon={MoneyIcon}
                current={currentPage === 'admin/fund_batches/IndexPage'}
              />
              <NavLinkItem
                name={'Fees'}
                href={admin_fee_batches_path()}
                icon={CurrencyCircleDollarIcon}
                current={currentPage === 'admin/fee_batches/IndexPage'}
              />
              <NavLinkItem
                name={t('admin.transfers.heading')}
                href={admin_transfers_path()}
                icon={TransactionsIcon}
                current={currentPage === 'admin/transfers/IndexPage'}
              />
            </ul>
          </li>
          <li>
            <div className="text-xs font-semibold leading-6 text-gray-400">
              Servicing
            </div>
            <ul className="-mx-2 mt-2 space-y-1">
              <NavLinkItem
                name={'Portfolios'}
                href={admin_portfolios_path()}
                icon={PortfolioIcon}
                current={currentPage === 'admin/portfolios/IndexPage'}
              />
            </ul>
          </li>
          <li>
            <div className="text-xs font-semibold leading-6 text-gray-400">
              {t('common.reports')}
            </div>
            <ul className="-mx-2 mt-2 space-y-1">
              <NavLinkItem
                name={t('admin.reports.revenue.title')}
                href={revenue_admin_reports_path()}
                icon={ChartLineUpIcon}
                current={currentPage === 'admin/reports/RevenuePage'}
              />
            </ul>
          </li>
          <li>
            <div className="text-xs font-semibold leading-6 text-gray-400">
              Settings
            </div>
            <ul className="-mx-2 mt-2 space-y-1">
              <NavLinkItem
                name={t('common.bank_accounts')}
                href={admin_bank_accounts_path()}
                icon={BankIcon}
                current={currentPage === 'admin/bank_accounts/IndexPage'}
              />
            </ul>
            <ul className="-mx-2 mt-2 space-y-1">
              <NavLinkItem
                name={'Users'}
                href={admin_users_path()}
                icon={UsersThreeIcon}
                current={currentPage === 'admin/users/IndexPage'}
              />
            </ul>
          </li>
        </ul>
      </nav>
    );
  };

  const UserNav = () => {
    const mfaPending = features.mfa ? !current_user.mfa_enrolled : false;

    return (
      <nav className="flex flex-1 flex-col">
        <ul className="flex flex-1 flex-col gap-y-7">
          {!mfaPending && (
            <li>
              <ul className="-mx-2 space-y-1">
                <NavLinkItem
                  name={t('common.dashboard')}
                  href={root_path()}
                  icon={HomeIcon}
                />
              </ul>
            </li>
          )}
          <li>
            <div className="text-xs font-semibold leading-6 text-gray-400">
              {t('common.account')}
            </div>
            <ul className="-mx-2 mt-2 space-y-1">
              {!mfaPending && (
                <NavLinkItem
                  name={t('common.manage_account')}
                  href={account_user_path()}
                  icon={GearIcon}
                  current={currentPage === 'user/AccountPage'}
                />
              )}
              {features.mfa && (
                <NavLinkItem
                  name={t('common.mfa')}
                  href={mfa_user_path()}
                  icon={MFAIcon}
                  current={currentPage === 'user/MFAPage'}
                />
              )}
            </ul>
          </li>
        </ul>
      </nav>
    );
  };

  return (
    <>
      <div className="flex h-16 shrink-0 items-center">
        <img alt="LawFi" src={logoUrl} className="h-9 w-auto mt-5" />
      </div>
      {isAdmin && isAdminNamespace && <AdminNav />}
      {!isAdminNamespace && !isUserRoute && <FirmNav />}
      {isUserRoute && <UserNav />}
    </>
  );
}
